/** Точка сборки: вход, выбор экрана, старт партии.
 *
 * Состояние приложения держится здесь, а не в каждом экране: баланс,
 * профиль и задача дня меняются в одном месте, а показываются в четырёх, и
 * дублировать их запросы значит рано или поздно показать разные числа
 * на соседних экранах.
 */

import { useCallback, useEffect, useState } from 'react'

import { ApiError } from '../src/api/client'
import {
  abandonGame,
  fetchCurrentGame,
  fetchDailyChallenge,
  fetchReferrals,
  fetchStats,
  fetchTechniques,
  fetchWallet,
  loginAsDev,
  loginWithTelegram,
  restartGame,
  startGame,
} from './api/endpoints'
import type {
  DailyChallenge,
  Difficulty,
  GameMode,
  GameState,
  PlayerStats,
  ReferralStats,
  User,
} from './api/types'
import { Dialog } from './components/Sheet'
import { CrystalsScreen } from './screens/CrystalsScreen'
import { GameScreen } from './screens/GameScreen'
import { HomeScreen } from './screens/HomeScreen'
import { RatingScreen } from './screens/RatingScreen'
import { SettingsScreen } from './screens/SettingsScreen'
import { ShopScreen } from './screens/ShopScreen'
import { TechniquesScreen } from './screens/TechniquesScreen'
import { formatTime } from './game/format'
import { loadSettings, updateSettings } from './game/settings'
import { t } from './i18n'
import { applyBoardTheme, getWebApp, isInsideTelegram, shareToChat } from './telegram/webapp'

type Screen =
  | 'loading'
  | 'home'
  | 'game'
  | 'rating'
  | 'shop'
  | 'crystals'
  | 'settings'
  | 'techniques'
  | 'error'

/** Загрузить необязательные данные, не роняя вход из-за их отсутствия. */
async function optional<T>(load: () => Promise<T>): Promise<T | null> {
  try {
    return await load()
  } catch {
    return null
  }
}

export function App() {
  const [screen, setScreen] = useState<Screen>('loading')
  const [user, setUser] = useState<User | null>(null)
  const [game, setGame] = useState<GameState | null>(null)
  const [currentGame, setCurrentGame] = useState<GameState | null>(null)
  const [balance, setBalance] = useState(0)
  const [daily, setDaily] = useState<DailyChallenge | null>(null)
  const [stats, setStats] = useState<PlayerStats | null>(null)
  const [referral, setReferral] = useState<ReferralStats | null>(null)
  const [techniques, setTechniques] = useState({ discovered: 0, total: 0 })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /** Партия, которую предлагается продолжить вместо новой. */
  const [conflict, setConflict] = useState<GameState | null>(null)
  const [pending, setPending] = useState<{ difficulty: Difficulty; mode: GameMode } | null>(null)
  /** Меняется при смене языка, чтобы перерисовать дерево целиком. */
  const [languageTick, setLanguageTick] = useState(0)

  const refresh = useCallback(async () => {
    setCurrentGame(await optional(fetchCurrentGame))
    const wallet = await optional(fetchWallet)
    if (wallet) setBalance(wallet.balance)
    setDaily(await optional(fetchDailyChallenge))
    setStats(await optional(fetchStats))
    const catalog = await optional(fetchTechniques)
    if (catalog) setTechniques({ discovered: catalog.discovered, total: catalog.total })
  }, [])

  useEffect(() => {
    void (async () => {
      try {
        const initData = getWebApp()?.initData
        const auth =
          isInsideTelegram() && initData
            ? await loginWithTelegram(initData)
            : await loginAsDev(Number(import.meta.env.VITE_DEV_TELEGRAM_ID ?? 777001))

        setUser(auth.user)
        applyBoardTheme(auth.user.theme)
        await loadSettings(auth.user.language_code)
        // Тактильный отклик спрашивают из кода без доступа к профилю —
        // переносим серверное значение в местные настройки.
        updateSettings({ haptics: auth.user.haptics_enabled })
        setLanguageTick((tick) => tick + 1)

        setReferral(await optional(fetchReferrals))
        await refresh()
        setScreen('home')
      } catch (failure) {
        setError(failure instanceof Error ? failure.message : t('app.error.login'))
        setScreen('error')
      }
    })()
  }, [refresh])

  const begin = useCallback(
    async (
      difficulty: Difficulty,
      { mode = 'normal' as GameMode, abandonCurrent = false, puzzle }: {
        mode?: GameMode
        abandonCurrent?: boolean
        puzzle?: number
      } = {},
    ) => {
      setBusy(true)
      setError(null)
      try {
        const started = await startGame(difficulty, { abandonCurrent, mode, puzzle })
        setGame(started)
        setCurrentGame(null)
        setScreen('game')
      } catch (failure) {
        if (failure instanceof ApiError && failure.code === 'active_game_exists' && failure.body.game) {
          // Не начинаем новую поверх старой молча: прогресс чужой, и
          // решение бросать его принимает игрок.
          setConflict(failure.body.game)
          setPending({ difficulty, mode })
        } else if (failure instanceof ApiError && failure.code === 'puzzle_already_played') {
          setError(t('dialog.puzzlePlayed'))
        } else {
          setError(failure instanceof Error ? failure.message : t('app.error.start'))
        }
      } finally {
        setBusy(false)
      }
    },
    [],
  )

  const goHome = useCallback(async () => {
    setGame(null)
    setScreen('home')
    await refresh()
  }, [refresh])

  const playAgain = useCallback(async () => {
    const difficulty = game?.difficulty ?? 'medium'
    const mode = game?.mode === 'daily' ? 'normal' : (game?.mode ?? 'normal')
    if (game && game.status === 'active') await abandonGame(game.id)
    await begin(difficulty, { mode, abandonCurrent: true })
  }, [begin, game])

  /** «Начать эту задачу снова» — та же доска, а не случайная новая. */
  const restart = useCallback(async (finished: GameState) => {
    setBusy(true)
    try {
      setGame(await restartGame(finished.id, true))
      setScreen('game')
    } catch {
      setError(t('app.error.start'))
    } finally {
      setBusy(false)
    }
  }, [])

  /**
   * Поделиться результатом.
   *
   * Ссылка ведёт на ту же задачу и остаётся реферальной: похвастаться
   * победой и позвать друга — одно действие, разделять их незачем.
   */
  const share = useCallback(
    (finished: GameState) => {
      const link = referral?.link
      if (!link) return
      shareToChat(
        link,
        t('result.shareText', {
          name: user?.display_name ?? '',
          level: t(`level.${finished.difficulty}`),
          time: formatTime(finished.elapsed_ms),
        }),
      )
    },
    [referral, user],
  )

  const openDaily = useCallback(() => {
    setScreen('home')
    // Уровень сервер возьмёт свой — у задачи дня он фиксирован.
    void begin(daily?.difficulty ?? 'hard', { mode: 'daily' })
  }, [begin, daily])

  if (screen === 'loading') {
    return (
      <div className="screen screen--center">
        <div className="loader">
          {Array.from({ length: 9 }, (_, index) => (
            <span className="loader__cell" key={index} />
          ))}
        </div>
        <span className="hint-text">{t('app.loading')}</span>
      </div>
    )
  }

  if (screen === 'error') {
    return (
      <div className="screen screen--center">
        <p className="error-text">{error}</p>
      </div>
    )
  }

  if (screen === 'rating') {
    return (
      <RatingScreen
        key={languageTick}
        playerName={user?.display_name ?? ''}
        onExit={() => void goHome()}
        onOpenDaily={openDaily}
      />
    )
  }

  if (screen === 'shop') {
    return (
      <ShopScreen
        key={languageTick}
        onExit={() => void goHome()}
        onThemeChange={(theme) => setUser((current) => (current ? { ...current, theme } : current))}
        onBalanceChange={setBalance}
        onOpenCrystals={() => setScreen('crystals')}
      />
    )
  }

  if (screen === 'crystals') {
    return (
      <CrystalsScreen
        key={languageTick}
        streak={stats?.current_streak ?? 0}
        onExit={() => setScreen(game ? 'game' : 'home')}
        onOpenDaily={openDaily}
      />
    )
  }

  if (screen === 'techniques') {
    return <TechniquesScreen key={languageTick} onExit={() => setScreen('settings')} />
  }

  if (screen === 'settings' && user) {
    return (
      <SettingsScreen
        key={languageTick}
        user={user}
        techniques={techniques}
        onExit={() => void goHome()}
        onUserChange={setUser}
        onLanguageChange={() => setLanguageTick((tick) => tick + 1)}
        onOpenTechniques={() => setScreen('techniques')}
        onInvite={() => {
          if (referral?.link) shareToChat(referral.link, t('result.inviteText'))
        }}
      />
    )
  }

  if (screen === 'game' && game) {
    return (
      <GameScreen
        key={`${game.id}:${languageTick}`}
        initialGame={game}
        balance={balance}
        digitFirst={user?.digit_first ?? false}
        autoClearNotes={user?.auto_clear_notes ?? true}
        dailyReward={daily?.max_reward ?? 0}
        previousBest={stats?.best_times?.[game.difficulty] ?? null}
        onBalanceChange={setBalance}
        onExit={() => void goHome()}
        onPlayAgain={() => void playAgain()}
        onRestart={(finished) => void restart(finished)}
        onOpenCrystals={() => setScreen('crystals')}
        onShare={share}
      />
    )
  }

  return (
    <>
      <HomeScreen
        key={languageTick}
        user={user}
        balance={balance}
        currentGame={currentGame}
        daily={daily}
        stats={stats}
        busy={busy}
        onContinue={() => {
          if (!currentGame) return
          setGame(currentGame)
          setScreen('game')
        }}
        onStart={(difficulty, mode) => void begin(difficulty, { mode })}
        onStartDaily={openDaily}
        onOpenRating={() => setScreen('rating')}
        onOpenShop={() => setScreen('shop')}
        onOpenSettings={() => setScreen('settings')}
      />

      {conflict && (
        <Dialog title={t('dialog.activeGame.title')} onDismiss={() => setConflict(null)}>
          <p className="hint-text" style={{ margin: 0, fontSize: 'var(--size-body)', lineHeight: 1.45 }}>
            {t('dialog.activeGame.body', {
              level: t(`level.${conflict.difficulty}`),
              time: formatTime(conflict.elapsed_ms),
              filled: conflict.filled_count,
            })}
          </p>
          <button
            type="button"
            className="button button--primary"
            onClick={() => {
              setGame(conflict)
              setConflict(null)
              setScreen('game')
            }}
          >
            {t('dialog.activeGame.continue')}
          </button>
          <button
            type="button"
            className="button button--danger"
            onClick={() => {
              const request = pending
              setConflict(null)
              setPending(null)
              if (request) void begin(request.difficulty, { ...request, abandonCurrent: true })
            }}
          >
            {t('dialog.activeGame.new')}
          </button>
        </Dialog>
      )}

      {error && !conflict && (
        <div className="banner banner--error" style={{ margin: 'var(--screen-padding)' }}>
          {error}
        </div>
      )}
    </>
  )
}
